import React, { useEffect } from 'react';
import { ArrowLeft, Shield, Database, Users, RefreshCw, Mail, GraduationCap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SprachMeisterPrivacy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-[#1E9BF0]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1E9BF0]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-[#1E9BF0]" />
            </div>
            <span className="font-semibold text-white">SprachMeister</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-24 h-24 rounded-3xl bg-white flex items-center justify-center mx-auto mb-6 shadow-lg shadow-black/10">
            <GraduationCap className="w-14 h-14 text-[#1E9BF0]" />
          </div>
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
            <Shield className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">Your Privacy Matters</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Privacy Policy
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            SprachMeister is designed with your privacy in mind. Learn how we protect your data while helping you master German.
          </p>
          <p className="text-white/60 text-sm mt-4">Last Updated: December 2024</p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto space-y-6">

          {/* Data Collection */}
          <div className="bg-white rounded-2xl p-8 shadow-lg shadow-black/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#1E9BF0] flex items-center justify-center">
                <Database className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1E9BF0]">Data Collection</h2>
            </div>
            <p className="text-gray-600 mb-4">
              SprachMeister is designed with your privacy in mind. We collect only the minimum data necessary to provide you with a great learning experience.
            </p>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start gap-3">
                <span className="text-[#1E9BF0] mt-1 font-bold">→</span>
                <span>Your voice recordings are processed locally on your device</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#1E9BF0] mt-1 font-bold">→</span>
                <span>Writing exercises are analyzed using secure AI services</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#1E9BF0] mt-1 font-bold">→</span>
                <span>Progress data is stored locally on your device</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#1E9BF0] mt-1 font-bold">→</span>
                <span>We do not sell your personal data to third parties</span>
              </li>
            </ul>
          </div>

          {/* Third-Party Services */}
          <div className="bg-white rounded-2xl p-8 shadow-lg shadow-black/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#1E9BF0] flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1E9BF0]">Third-Party Services</h2>
            </div>
            <p className="text-gray-600 mb-4">
              To provide AI-powered language analysis, we may use the following services:
            </p>
            <ul className="space-y-3 text-gray-600 mb-4">
              <li className="flex items-start gap-3">
                <span className="text-[#1E9BF0] mt-1 font-bold">→</span>
                <span>Speech-to-Text services for voice recognition</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#1E9BF0] mt-1 font-bold">→</span>
                <span>AI language models for conversation and correction</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#1E9BF0] mt-1 font-bold">→</span>
                <span>Text-to-Speech services for pronunciation examples</span>
              </li>
            </ul>
            <p className="text-gray-500 text-sm">
              These services process data according to their respective privacy policies. No personal identifying information is shared with these services.
            </p>
          </div>

          {/* Data Storage */}
          <div className="bg-white rounded-2xl p-8 shadow-lg shadow-black/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#1E9BF0] flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1E9BF0]">Data Storage</h2>
            </div>
            <p className="text-gray-600">
              All your learning progress, conversation history, and writing exercises are stored locally on your device. You have full control over your data and can delete it at any time through the app settings.
            </p>
          </div>

          {/* Children's Privacy */}
          <div className="bg-white rounded-2xl p-8 shadow-lg shadow-black/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#1E9BF0] flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1E9BF0]">Children's Privacy</h2>
            </div>
            <p className="text-gray-600">
              SprachMeister does not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
            </p>
          </div>

          {/* Changes to Policy */}
          <div className="bg-white rounded-2xl p-8 shadow-lg shadow-black/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#1E9BF0] flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1E9BF0]">Changes to This Policy</h2>
            </div>
            <p className="text-gray-600">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-2xl p-8 text-center shadow-lg shadow-black/5">
            <div className="w-16 h-16 rounded-2xl bg-[#1E9BF0] flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-3">Need Help?</h2>
            <p className="text-gray-600 mb-6 max-w-lg mx-auto">
              We're here to help you on your German learning journey. Reach out with any questions or feedback about our privacy practices.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="mailto:tymarbeit@gmail.com"
                className="inline-flex items-center gap-2 bg-[#1E9BF0] hover:bg-[#1788D9] transition-colors text-white px-6 py-3 rounded-full font-medium shadow-lg shadow-[#1E9BF0]/30"
              >
                <Mail className="w-5 h-5" />
                tymarbeit@gmail.com
              </a>
              <a
                href="mailto:tymur@abkiliamov.com"
                className="inline-flex items-center gap-2 bg-[#1E9BF0] hover:bg-[#1788D9] transition-colors text-white px-6 py-3 rounded-full font-medium shadow-lg shadow-[#1E9BF0]/30"
              >
                <Mail className="w-5 h-5" />
                tymur@abkiliamov.com
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6 text-center">
        <p className="text-white/60 text-sm">
          &copy; 2024 SprachMeister. All rights reserved.
        </p>
        <div className="flex items-center justify-center gap-4 mt-3">
          <a
            href="https://github.com/deeplearnproger/sprachmeister"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/60 hover:text-white transition-colors text-sm"
          >
            GitHub
          </a>
          <span className="text-white/30">·</span>
          <button
            onClick={() => navigate('/')}
            className="text-white/60 hover:text-white transition-colors text-sm"
          >
            Portfolio
          </button>
        </div>
      </footer>
    </div>
  );
};

export default SprachMeisterPrivacy;
